import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from './useTranslation';
import { getCitySuggestions } from './services/api/geocodingApi';

/** Формат подсказки города (совместим с отображением в компоненте). */
export type CitySuggestion = {
  _normalized_city: string;
  state?: string;
  country: string;
  geometry: { lat: number; lng: number };
};

/**
 * CityInput Component
 *
 * Fetches city suggestions via backend (/api/v1/geocoding/suggestions), which calls OpenCage
 * so the API key is not exposed. Shows a dropdown with city, region, country and allows selection.
 */

// Props for the CityInput component
type CityInputProps = {
  value: string;
  onChange: (value: string) => void;
  onSelectGeometry?: (geometry: { lat: number; lng: number }) => void;
  label: string;
  required?: boolean;
  language: string;
  isInvalid?: boolean;
  compact?: boolean;
};

const CityInput: React.FC<CityInputProps> = ({ value, onChange, onSelectGeometry, label, required = false, language, isInvalid = false, compact = false }) => {
  const [inputValue, setInputValue] = useState(value);
  const [suggestions, setSuggestions] = useState<CitySuggestion[]>([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const suggestionsRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const debounceTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Get translations
  const { t } = useTranslation(language);

  // Update local state when prop value changes
  useEffect(() => {
    setInputValue(value);
  }, [value]);

  // Handle click outside to close suggestions
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        suggestionsRef.current && 
        !suggestionsRef.current.contains(event.target as Node) &&
        inputRef.current &&
        !inputRef.current.contains(event.target as Node)
      ) {
        console.log('Click outside detected, hiding suggestions');
        setShowSuggestions(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);
  
  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
      }
    };
  }, []);

  // Fetch city suggestions via backend (OpenCage key stays on server)
  const fetchCitySuggestionsFromAPI = async (query: string) => {
    if (query.length < 3) {
      setSuggestions([]);
      setShowSuggestions(false);
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const lang = language ? language.split('-')[0] : 'en';
      const raw = await getCitySuggestions(query, lang);
      const items = Array.isArray(raw) ? raw : [];
      const citySuggestions: CitySuggestion[] = items.map((item: { normalized_city?: string; state?: string; country?: string; lat?: number; lng?: number }) => ({
        _normalized_city: item.normalized_city ?? '',
        state: item.state ?? undefined,
        country: item.country ?? '',
        geometry: { lat: item.lat ?? 0, lng: item.lng ?? 0 },
      })).filter((s) => s._normalized_city !== '');

      const sortedSuggestions = [...citySuggestions].sort((a, b) => {
        if (a._normalized_city.toLowerCase() === query.toLowerCase()) return -1;
        if (b._normalized_city.toLowerCase() === query.toLowerCase()) return 1;
        const aStarts = a._normalized_city.toLowerCase().startsWith(query.toLowerCase());
        const bStarts = b._normalized_city.toLowerCase().startsWith(query.toLowerCase());
        if (aStarts && !bStarts) return -1;
        if (!aStarts && bStarts) return 1;
        const aContains = a._normalized_city.toLowerCase().includes(query.toLowerCase());
        const bContains = b._normalized_city.toLowerCase().includes(query.toLowerCase());
        if (aContains && !bContains) return -1;
        if (!aContains && bContains) return 1;
        return 0;
      });

      setSuggestions(sortedSuggestions);
      setShowSuggestions(sortedSuggestions.length > 0);
    } catch (err) {
      console.error('Error fetching city suggestions:', err);
      setError('Failed to fetch city suggestions');
      setSuggestions([]);
    } finally {
      setIsLoading(false);
    }
  };

  // Handle input change
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    setInputValue(newValue);
    
    // Call the parent onChange handler
    onChange(newValue);
    
    // Clear suggestions if input is less than 3 characters
    if (newValue.length < 3) {
      setSuggestions([]);
      setShowSuggestions(false);
    } else {
      // If input has 3 or more characters, fetch suggestions
      // Use debounce to avoid too many API calls
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
      }
      
      // Set a new timeout to fetch suggestions after 300ms
      debounceTimeoutRef.current = setTimeout(() => {
        console.log('Debounced search triggered for:', newValue);
        fetchCitySuggestionsFromAPI(newValue);
        // Ensure suggestions are shown when typing
        setShowSuggestions(true);
      }, 300); // 300ms debounce
    }
  };
  
  // Handle blur event to fetch suggestions when user switches away from the input
  const handleInputBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    // Prevent immediate hiding of suggestions to allow clicks on suggestion items
    setTimeout(() => {
      // Check if the related target is within the suggestions dropdown
      if (suggestionsRef.current && !suggestionsRef.current.contains(e.relatedTarget as Node)) {
        // Only fetch suggestions if the input has at least 3 characters
        if (inputValue.length >= 3) {
          console.log('Blur event triggered fetch for:', inputValue);
          fetchCitySuggestionsFromAPI(inputValue);
        }
      }
    }, 150); // Small delay to allow click events on suggestions to fire first
  };
  
  // Handle input focus to fetch suggestions immediately when focusing on a field with content
  const handleInputFocus = () => {
    if (inputValue.length >= 3) {
      console.log('Focus event triggered fetch for:', inputValue);
      fetchCitySuggestionsFromAPI(inputValue);
      setShowSuggestions(true);
    }
  };

  // Handle suggestion selection
  const handleSelectSuggestion = (suggestion: CitySuggestion) => {
    console.log('Suggestion selected:', suggestion._normalized_city);
    
    // Format the address using _normalized_city, state, and country
    const formattedAddress = `${suggestion._normalized_city}${suggestion.state ? `, ${suggestion.state}` : ''}, ${suggestion.country}`;
    setInputValue(formattedAddress);
    onChange(formattedAddress);
    
    // If onSelectGeometry callback is provided, call it with the geometry data
    if (onSelectGeometry && suggestion.geometry) {
      onSelectGeometry(suggestion.geometry);
    }
    
    // Hide suggestions after selection
    setShowSuggestions(false);
    
    // Clear any pending debounced searches
    if (debounceTimeoutRef.current) {
      clearTimeout(debounceTimeoutRef.current);
      debounceTimeoutRef.current = null;
    }
  };

  const labelCl = compact
    ? 'block text-[10px] font-semibold uppercase tracking-wide text-stone-500 dark:text-slate-400 mb-0.5 leading-tight truncate'
    : 'block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2';
  const inputCl = compact
    ? `w-full h-9 pl-8 pr-8 text-sm rounded-lg border bg-white/90 dark:bg-slate-800/80 dark:text-white focus:ring-2 focus:ring-amber-500/35 focus:border-amber-400 transition-shadow ${
        isInvalid ? 'border-red-500 dark:border-red-400 ring-2 ring-red-500/25' : 'border-amber-200/70 dark:border-slate-600'
      }`
    : `w-full pl-10 pr-10 py-2.5 border ${
        isInvalid
          ? 'border-red-500 dark:border-red-400'
          : 'border-gray-300 dark:border-gray-600'
      } rounded-lg shadow-sm focus:border-blue-500 focus:outline-none dark:bg-gray-700 dark:text-white`;
  const iconCl = compact ? 'h-4 w-4' : 'h-5 w-5';
  const iconWrapCl = compact ? 'pl-2.5' : 'pl-3';
  const loaderTop = compact ? 'top-7' : 'top-9';

  return (
    <div className="relative min-w-0">
      <label className={labelCl}>
        {label}{required ? ' *' : ''}
      </label>
      <div className="relative">
        <div className={`absolute inset-y-0 left-0 ${iconWrapCl} flex items-center pointer-events-none`}>
          <svg xmlns="http://www.w3.org/2000/svg" className={`${iconCl} text-gray-500 dark:text-gray-400`} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"></path>
            <circle cx="12" cy="10" r="3"></circle>
          </svg>
        </div>
        <input
          ref={inputRef}
          type="text"
          value={inputValue}
          onChange={handleInputChange}
          onBlur={handleInputBlur}
          onFocus={handleInputFocus}
          className={inputCl}
        />
      </div>
      
      {/* Loading indicator */}
      {isLoading && (
        <div className={`absolute right-2.5 ${loaderTop}`}>
          <svg className="animate-spin h-4 w-4 text-emerald-500 dark:text-emerald-400" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
          </svg>
        </div>
      )}
      
      {/* Error message */}
      {error && <p className="text-red-500 dark:text-red-400 text-sm mt-1">{error}</p>}
      
      {/* Suggestions dropdown */}
      {showSuggestions && suggestions.length > 0 && (
        <div 
          ref={suggestionsRef}
          className="absolute z-10 mt-1 w-full bg-white dark:bg-gray-800 shadow-lg rounded-md border border-gray-200 dark:border-gray-700 max-h-60 overflow-auto"
        >
          {suggestions.map((suggestion, index) => (
            <div
              key={index}
              className="px-4 py-2 hover:bg-gray-100 dark:hover:bg-gray-700 cursor-pointer"
              onClick={() => handleSelectSuggestion(suggestion)}
            >
              <div className="dark:text-white">
                {suggestion._normalized_city}{suggestion.state ? `, ${suggestion.state}` : ''}, {suggestion.country}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default CityInput;